import {
  DragSourceMonitor,
  DropTargetMonitor,
  useDrop,
  useDrag,
} from 'react-dnd';
import { MutableRefObject } from 'react';

interface IDragEntity {
  type: string;
  dataItem: any;
  dataIndex: number;
}

/**
 * This hook sets up drag & drop support for the items in a re-orderable list
 * @param elementRef ref to the item that can be dragged and dropped on
 * @param dataItem the data item associated with the ui element
 * @param index the list index of the given data item
 * @param changeOrderCallback A method that performs the actual re-ordering of the data items within the list
 * @param afterDropCallback An optional callback that will be triggered after a drop action is completed
 * @param dragItemType optional item type, defaults to 'ReorderDragDrop'
 */
const useReorderDragDrop = (
  elementRef: MutableRefObject<null>,
  dataItem: any,
  index: number,
  changeOrderCallback: (oldIndex: number, newIndex: number) => void,
  afterDropCallback?: () => void,
  dragItemType = 'ReorderDragDrop'
): any => {
  const [, drop] = useDrop({
    accept: dragItemType,
    drop: () => {
      if (afterDropCallback) {
        afterDropCallback();
      }
    },
    hover(item: IDragEntity, monitor: DropTargetMonitor): void {
      if (!elementRef.current) {
        return;
      }
      const dragIndex = item.dataIndex;
      const hoverIndex = index;
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }
      // Determine rectangle on screen
      const hoverBoundingRect = (elementRef.current as any)?.getBoundingClientRect();
      if (!hoverBoundingRect) {
        return;
      }
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Determine mouse position
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) {
        return;
      }
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Time to actually perform the action
      changeOrderCallback(dragIndex, hoverIndex);
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.dataIndex = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag<
    IDragEntity,
    any,
    { isDragging: boolean }
  >({
    item: { type: dragItemType, dataItem, dataIndex: index },
    collect: (monitor: DragSourceMonitor): any => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(elementRef));

  return {
    isDragging,
    drag,
    preview,
  };
};

export default useReorderDragDrop;
